/**
 * PosthogRecordingGate
 * ====================
 * Única fuente de verdad sobre si la sesión se graba (session replay).
 * `main.jsx` arranca SIEMPRE con `disable_session_recording: true`; este
 * componente es el único que enciende. Decisiones §41.7.
 *
 * NO se graba si:
 *  0. el entorno no es prod (local + staging — ahorra quota y no ensucia
 *     con sesiones de dev/QA)
 *  1. el usuario es admin (`is_staff` / `role === 'admin'`)
 *  2. hay una sesión de impersonación de soporte activa
 *  3. la ruta es el LinkTree público `/en1/:id`
 *  4. la ruta es la landing de escaneo `/redirect`
 *
 * Por qué el (4) importa más que los otros tres juntos: el nginx de prod solo
 * enruta `/api/`, `/admin/`, `/static/` y `/widget*` a Django, así que
 * `/redirect/?id=` — la URL que llevan TODOS los QR — la sirve el SPA, no el
 * handler server-side de `qr_project/views.py`. Son ~130.000 escaneos/mes en
 * producción contra 5.000 recordings/mes del free tier. Sin esta exclusión la
 * quota se agota en días con vídeos de un spinner de dos segundos y las
 * sesiones que sí importan (clientes en el dashboard) dejan de grabarse el
 * resto del mes. Los EVENTOS (pageview, autocapture) siguen fluyendo desde
 * esas rutas: lo que se apaga es el vídeo, no la analítica del funnel.
 *
 * Se espera a que `me()` resuelva (`isLoadingAuth`) antes de encender. Si no,
 * el primer render (`user === null`) arrancaría la grabación de un admin y
 * PostHog ya factura esos segundos: `stopSessionRecording()` para el recorder
 * pero NO descarta el buffer (el `discard()` de posthog-js solo se usa en
 * sampling), y el flush de `pagehide` los manda igual.
 *
 * Va montado junto a <NavigationTracker /> en `App.jsx`, no dentro de
 * <AuthenticatedApp />: necesita el Router (`useLocation`) y ese componente
 * lee `window.location` a propósito, sin suscribirse a los cambios de ruta —
 * suscribirlo cambiaría cuándo dispara su `navigateToLogin()`, que es un
 * `window.location.href` en fase de render.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';

import { useAuth } from '@/lib/AuthContext';

/**
 * Minúsculas + sin barras finales. La forma canónica de la landing de escaneo
 * es `/redirect/?id=` CON barra (así la generan el backend y el front), y
 * `useLocation().pathname` la devuelve tal cual, así que comparar contra
 * '/redirect' pelado fallaría justo en el 100% del tráfico real. Mismo
 * tratamiento que hace `AuthenticatedApp` con su `normalizedPath`.
 */
function normalizePath(pathname) {
  return (pathname || '').toLowerCase().replace(/\/+$/, '');
}

/** Rutas públicas sin valor de replay, ya normalizadas. */
function isBlockedRoute(path) {
  if (path === '/redirect') return true;                    // landing de escaneo (+ alias /Redirect)
  return path === '/en1' || path.startsWith('/en1/');       // LinkTree público
}

/**
 * Puro y exportado aparte para poder testear las reglas sin montar React.
 * Devuelve true = NO grabar.
 */
export function isRecordingBlocked({ isLoadingAuth, user, impersonation, pathname }) {
  if (isLoadingAuth) return true;                           // aún no sabemos quién es
  if (isBlockedRoute(normalizePath(pathname))) return true;
  if (impersonation?.active) return true;                   // sesión de soporte
  return !!(user?.is_staff || user?.role === 'admin');      // admin
}

export default function PosthogRecordingGate() {
  const { user, impersonation, isLoadingAuth } = useAuth();
  const { pathname } = useLocation();

  useEffect(() => {
    // Fuera de prod el gate es INERTE a propósito: `main.jsx` ya arranca sin
    // grabar, así que no hay nada que apagar, y así el toggle manual de QA
    // (`__ph.on()`, §41.7) sigue vivo — si apagáramos aquí, encenderlo a mano
    // en staging duraría hasta el siguiente cambio de ruta.
    if (import.meta.env.VITE_APP_ENV !== 'prod') return;
    if (!posthog.__loaded) return;

    const blocked = isRecordingBlocked({ isLoadingAuth, user, impersonation, pathname });
    // API pública desde posthog-js 1.376.2; `posthog.sessionRecording` es
    // opcional (el módulo carga lazy) y leer `.started` a pelo revienta o
    // miente mientras tanto.
    const started = posthog.sessionRecordingStarted();

    if (blocked && started) posthog.stopSessionRecording();
    else if (!blocked && !started) posthog.startSessionRecording();
  }, [user, impersonation, isLoadingAuth, pathname]);

  return null;
}
